/**
 * V2 category chips — horizontally scrollable filter row.
 * Counts reflect the currently loaded feed.
 * Accessibility: native buttons (keyboard for free) with aria-pressed
 * carrying the selected state — these are filters, not tabs.
 */
import { escapeHtml } from '../../assets/js/utils.js';
import { slugify } from '../../assets/js/shared/slugify.js';
import { categoryIcon } from './icons.js';

/**
 * @param {Array} categories - [{ id, name, emoji, count }] — count is
 *   unique LOCATIONS like V1 (discovery.js:469-487), not yoinks
 * @param {string|null} selectedId
 * @param {Array<string>} followedIds - followed chips wear a ✓ (V1 :519)
 * @param {string} leading - extra chip markup pinned to the START of the
 *   scroller. Discover's "All categories →" used to sit OUTSIDE the
 *   scroller, which clipped whichever chip it overlapped (2026-07-27);
 *   inside, the row is one uniform thing that scrolls as a whole. It moved
 *   from the end to the front on 2026-09-01 (user ask): the door to every
 *   category — the place you pick which ones to follow — belongs where the
 *   row starts, not past the last chip you can scroll to.
 *
 * The "All" chip is the way BACK to the unfiltered feed, so it renders
 * only while a category is selected (same 2026-09-01 ask: "we don't need
 * the 'all' unless one of the followed categories is picked"). Unfiltered,
 * an active All chip announced nothing the cards weren't already showing.
 */
export function categoryChips(categories, selectedId, followedIds = [], leading = '') {
  const followed = new Set(followedIds);
  const all = selectedId
    ? `
    <button class="v2-chip" data-category-id="" aria-pressed="false">
      All
    </button>`
    : '';

  const rest = categories
    .map(
      (cat) => `
    <button class="v2-chip ${selectedId === cat.id ? 'chip-active' : ''}" data-category-id="${cat.id}"
      data-category-nav="${slugify(cat.name)}" aria-pressed="${selectedId === cat.id}">
      ${categoryIcon(cat, 16)}
      <span>${followed.has(cat.id) ? '✓ ' : ''}${escapeHtml(cat.name)}</span>
      <span class="chip-count">(${cat.count})</span>
    </button>`
    )
    .join('');

  return `<div class="v2-chips" role="group" aria-label="Filter by category">${leading}${all}${rest}</div>`;
}
